import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  FiUsers,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiEye,
  FiDownload,
  FiPhone,
  FiSearch,
  FiCheck,
  FiXCircle,
  FiSlash,
  FiImage,
  FiGrid,
  FiList,
  FiCalendar,
} from "react-icons/fi";
import {
  fetchChallans,
  fetchAllChallans,
  cancelChallan,
} from "../../../redux/actions/survey-management/challanActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../../redux/actions/internal/internalActions";

const Challan = () => {
  const dispatch = useDispatch();
  const { loading, challans, pagination } = useSelector(
    (state) =>
      state.challans || {
        loading: false,
        challans: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
      },
  );

  const { districts, municipalities } = useSelector(
    (state) => state.internal || { districts: [], municipalities: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 50, 100, 200, 500];
  const [viewMode, setViewMode] = useState("table");
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    search: "",
    payment_status: "",
    offense_id: "",
    start_date: "",
    end_date: "",
  });
  const [imageModal, setImageModal] = useState({
    open: false,
    images: [],
    title: "",
    currentIndex: 0,
  });
  const [cancelModal, setCancelModal] = useState({
    open: false,
    challanId: null,
    remarks: "",
  });

  useEffect(() => {
    dispatch(fetchChallans(currentPage, limit, filters));
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    filters.search,
    filters.payment_status,
    filters.offense_id,
    filters.start_date,
    filters.end_date,
  ]);

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchChallans(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchChallans(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleCancelChallan = async () => {
    if (!cancelModal.challanId) return;

    try {
      const result = await dispatch(
        cancelChallan(cancelModal.challanId, cancelModal.remarks),
      );

      if (result?.meta?.status) {
        setCancelModal({ open: false, challanId: null, remarks: "" });
        dispatch(fetchChallans(currentPage, limit, filters));
      }
    } catch (error) {
      console.error("Cancel error:", error);
    }
  };

  const openCancelModal = (challanId) => {
    setCancelModal({
      open: true,
      challanId: challanId,
      remarks: "",
    });
  };
  const handleExportToExcel = async () => {
    try {
      const toastId = toast.loading("Preparing Challan Data Excel...");

      if (challans.length === 0) {
        toast.error("No data to export");
        toast.dismiss(toastId);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Challans");

      const headers = [
        "Sl. No.",
        "Challan No.",
        "Offender Name",
        "Offender Phone",
        "Offender Address",
        "Vehicle No.",
        "Offense Name",
        "Fine Amount",
        "Payment Status",
        "District",
        "Municipality",
        "Officer Name",
        "Officer Phone",
        // "Remarks",
        "Latitude",
        "Longitude",
        "Date",
        "Cancelled Remarks",
      ];

      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCC6633" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      challans.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.challan_no || "N/A",
          item.offender_name || "N/A",
          item.offender_phone || "N/A",
          item.offender_address || "N/A",
          item.vehicle_no || "N/A",
          item.offense_name || "N/A",
          item.fine_amount || "0",
          item.payment_status || "N/A",
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.officer_name || "N/A",
          item.officer_phone || "N/A",
          // item.remarks || "N/A",
          item.latitude || "N/A",
          item.longitude || "N/A",
          item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-IN")
            : "N/A",
          item.cancelled_remarks || "N/A",
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF0F8FF" },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFFFF" },
            };
          }
        });
      });

      const colWidths = [
        8, 18, 25, 18, 30, 18, 25, 15, 18, 20, 25, 22, 18, 15, 15, 15, 30,
      ];
      worksheet.columns = colWidths.map((width) => ({ width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(new Blob([buffer]), `Challans_${dateStr}.xlsx`);

      toast.success(`Exported ${challans.length} records successfully!`, {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export data");
      console.error(error);
    }
  };

  const handleBulkExportToExcel = async () => {
    try {
      const toastId = toast.loading("Fetching all records...");

      const allChallans = await fetchAllChallans(filters, 2000000);

      if (allChallans.length === 0) {
        toast.error("No data to export");
        toast.dismiss(toastId);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Challans");

      const headers = [
        "Sl. No.",
        "Challan No.",
        "Offender Name",
        "Offender Phone",
        "Offender Address",
        "Vehicle No.",
        "Offense Name",
        "Fine Amount",
        "Payment Status",
        "District",
        "Municipality",
        "Officer Name",
        "Officer Phone",
        // "Remarks",
        "Latitude",
        "Longitude",
        "Date",
        "Cancelled Remarks",
      ];

      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCC6633" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      allChallans.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.challan_no || "N/A",
          item.offender_name || "N/A",
          item.offender_phone || "N/A",
          item.offender_address || "N/A",
          item.vehicle_no || "N/A",
          item.offense_name || "N/A",
          item.fine_amount || "0",
          item.payment_status || "N/A",
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.officer_name || "N/A",
          item.officer_phone || "N/A",
          // item.remarks || "N/A",
          item.latitude || "N/A",
          item.longitude || "N/A",
          item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-IN")
            : "N/A",
          item.cancelled_remarks || "N/A",
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF0F8FF" },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFFFF" },
            };
          }
        });
      });

      const colWidths = [
        8, 18, 25, 18, 30, 18, 25, 15, 18, 20, 25, 22, 18, 15, 15, 15, 30,
      ];
      worksheet.columns = colWidths.map((width) => ({ width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(new Blob([buffer]), `Challans_All_${dateStr}.xlsx`);

      toast.success(`Exported ${allChallans.length} records successfully!`, {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export");
      console.error("Bulk export error:", error);
    }
  };

  const openImageModal = (images, title) => {
    if (images && images.length > 0) {
      setImageModal({
        open: true,
        images: images,
        title: title || "Challan Images",
        currentIndex: 0,
      });
    } else {
      toast.error("No images available");
    }
  };

  const navigateImage = (direction) => {
    setImageModal((prev) => ({
      ...prev,
      currentIndex: prev.currentIndex + direction,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 md:p-6">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200/30 dark:bg-orange-900/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/30 dark:bg-blue-900/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
              <FiUsers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
                Challan Management
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Manage and view challan data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleBulkExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export All</span>
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-slate-200 dark:border-slate-600 text-base"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          {
            label: "Total Challans",
            value: pagination.totalRecords || 0,
            icon: FiUsers,
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
            borderColor: "border-orange-200 dark:border-orange-800",
            iconBg: "from-orange-500 to-orange-600",
          },
          {
            label: "Total Paid",
            value: challans.filter((o) => o.payment_status === "Paid").length,
            icon: FiUsers,
            bgColor: "bg-green-50 dark:bg-green-900/20",
            borderColor: "border-green-200 dark:border-green-800",
            iconBg: "from-green-500 to-green-600",
          },
          {
            label: "Total Pending",
            value: challans.filter((o) => o.payment_status === "Pending")
              .length,
            icon: FiUsers,
            bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
            borderColor: "border-yellow-200 dark:border-yellow-800",
            iconBg: "from-yellow-500 to-yellow-600",
          },
          {
            label: "Total Fine Amount",
            value: `₹${challans.reduce((acc, o) => acc + (parseFloat(o.fine_amount) || 0), 0).toLocaleString()}`,
            icon: FiUsers,
            bgColor: "bg-purple-50 dark:bg-purple-900/20",
            borderColor: "border-purple-200 dark:border-purple-800",
            iconBg: "from-purple-500 to-purple-600",
          },
        ].map((stat, idx) => (
          <div
            key={idx}
            className={`${stat.bgColor} border ${stat.borderColor} rounded-xl shadow-sm p-3 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-white mt-0.5">
                  {stat.value}
                </p>
              </div>
              <div
                className={`p-2.5 bg-gradient-to-br ${stat.iconBg} rounded-xl shadow-lg`}
              >
                <stat.icon className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  ...filters,
                  district_id: value,
                  municipality_id: "",
                });
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white min-w-[180px]"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white disabled:opacity-50 min-w-[180px]"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            <select
              value={filters.payment_status}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  payment_status: e.target.value,
                });
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white min-w-[150px]"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by challan number"
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white w-full min-w-[250px]"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <FiCalendar className="text-slate-400 w-4 h-4" />
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    start_date: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              />
              <span className="text-slate-500 dark:text-slate-400">to</span>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    end_date: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              />
            </div>

            {(filters.district_id ||
              filters.municipality_id ||
              filters.search ||
              filters.payment_status ||
              filters.start_date ||
              filters.end_date) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                    search: "",
                    payment_status: "",
                    offense_id: "",
                    start_date: "",
                    end_date: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                }}
                className="flex items-center gap-1.5 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm font-medium whitespace-nowrap"
              >
                <FiX className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "table"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiList className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiGrid className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 dark:text-slate-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-orange-200 dark:border-orange-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : challans.length > 0 ? (
          <>
            {viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        #
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Challan No.
                      </th>
                      <th className="text-center py-2.5 px-3 min-w-[90px] text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Offender
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="text-center py-2.5 px-3 min-w-[150px] text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Offense
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Fine
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        District
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Municipality
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Officer
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Images
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="text-center py-2.5 px-3 min-w-[110px] text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Cancelled Remarks
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {challans.map((item, index) => (
                      <tr
                        key={item.id}
                        className="hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors duration-150"
                      >
                        <td className="py-2.5 px-3 text-center text-sm text-slate-500 dark:text-slate-400">
                          {(pagination.page - 1) * limit + index + 1}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm font-mono font-medium text-slate-800 dark:text-white">
                          {item.challan_no || "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          {item.offender_name || "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          <div className="flex items-center justify-center gap-1">
                            <FiPhone className="w-3 h-3" />
                            {item.offender_phone || "N/A"}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm font-semibold text-slate-600 dark:text-slate-300">
                          {item.offense_name || "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm font-medium text-pink-600 dark:text-pink-400">
                          ₹{item.fine_amount || "0"}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.payment_status === "Paid"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : item.payment_status === "Cancelled"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                  : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                            }`}
                          >
                            {item.payment_status || "N/A"}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          {item.district_name || "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          {item.municipality_name || "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          <div>
                            <div>{item.officer_name || "N/A"}</div>
                            {item.officer_phone && (
                              <div className="flex items-center justify-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                <FiPhone className="w-3 h-3" />
                                {item.officer_phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {item.images && item.images.length > 0 ? (
                            <button
                              onClick={() =>
                                openImageModal(
                                  item.images,
                                  `Challan ${item.challan_no}`,
                                )
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg transition-colors text-sm"
                            >
                              <FiImage className="w-4 h-4" />
                              <span>{item.images.length}</span>
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">
                              No images
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                          {item.created_at
                            ? new Date(item.created_at).toLocaleDateString(
                                "en-IN",
                              )
                            : "N/A"}
                        </td>
                        <td className="py-2.5 px-3 text-center italic text-xs text-red-600 dark:text-red-400">
                          "{item.cancelled_remarks}"
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {!item.cancelled_remarks &&
                            item.payment_status !== "Paid" && (
                              <button
                                onClick={() => openCancelModal(item.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white border border-red-700 dark:border-red-700 rounded-md text-xs font-medium transition-colors duration-200"
                                title="Cancel Challan"
                              >
                                <FiXCircle className="w-3.5 h-3.5" />
                                Cancel
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {challans.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-slate-700/50 rounded-xl shadow-lg border-2 border-orange-200 dark:border-orange-700 p-4 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-lg">
                          #{item.id} - {item.challan_no || "N/A"}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          {item.offender_name || "N/A"}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          <FiPhone className="inline w-3 h-3 mr-1" />
                          {item.offender_phone || "N/A"}
                        </p>
                      </div>
                      <div className="flex gap-1.5 ml-2">
                        {item.images && item.images.length > 0 && (
                          <button
                            onClick={() =>
                              openImageModal(
                                item.images,
                                `Challan ${item.challan_no}`,
                              )
                            }
                            className="p-1.5 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="View Images"
                          >
                            <FiImage className="w-4 h-4" />
                          </button>
                        )}
                        {!item.cancelled_remarks &&
                          item.payment_status !== "Paid" && (
                            <button
                              onClick={() => openCancelModal(item.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white border border-red-700 dark:border-red-700 rounded-md text-xs font-medium transition-colors duration-200"
                              title="Cancel Challan"
                            >
                              <FiXCircle className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                          )}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Offense
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.offense_name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          Fine Amount
                        </span>
                        <span className="font-semibold text-pink-600 dark:text-pink-400">
                          ₹{item.fine_amount || "0"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          Status
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.payment_status === "Paid"
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                              : item.payment_status === "Cancelled"
                                ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                          }`}
                        >
                          {item.payment_status || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          Location
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.district_name || "N/A"} -{" "}
                          {item.municipality_name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">
                          Date
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.created_at
                            ? new Date(item.created_at).toLocaleDateString(
                                "en-IN",
                              )
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Cancelled Remarks
                        </span>
                        <span className="py-2.5 px-3 text-right italic text-xs text-red-600 dark:text-red-400">
                          "{item.cancelled_remarks}"
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 gap-3">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const current = currentPage;
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (current <= 3) {
                          pages.push(1, 2, 3, 4, "...", totalPages);
                        } else if (current >= totalPages - 2) {
                          pages.push(
                            1,
                            "...",
                            totalPages - 3,
                            totalPages - 2,
                            totalPages - 1,
                            totalPages,
                          );
                        } else {
                          pages.push(
                            1,
                            "...",
                            current - 1,
                            current,
                            current + 1,
                            "...",
                            totalPages,
                          );
                        }
                      }

                      return pages.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-slate-500 dark:text-slate-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                              current === page
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      );
                    })()}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <FiUsers className="w-10 h-10 text-slate-300 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              No challans found
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              No challans match your current filters
            </p>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {imageModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() =>
            setImageModal({
              open: false,
              images: [],
              title: "",
              currentIndex: 0,
            })
          }
        >
          <div className="relative max-w-4xl w-full mx-4">
            <button
              onClick={() =>
                setImageModal({
                  open: false,
                  images: [],
                  title: "",
                  currentIndex: 0,
                })
              }
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl"
            >
              <FiX className="w-6 h-6" />
            </button>

            {imageModal.images.length > 0 && (
              <div className="relative">
                <img
                  src={imageModal.images[imageModal.currentIndex]}
                  alt={`${imageModal.title} - ${imageModal.currentIndex + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />

                {imageModal.images.length > 1 && (
                  <div className="absolute inset-x-0 bottom-4 flex items-center justify-between px-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateImage(-1);
                      }}
                      disabled={imageModal.currentIndex === 0}
                      className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors disabled:opacity-40"
                    >
                      <FiChevronLeft className="w-6 h-6" />
                    </button>
                    <span className="text-white bg-black/50 px-3 py-1 rounded-lg text-sm">
                      {imageModal.currentIndex + 1} / {imageModal.images.length}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateImage(1);
                      }}
                      disabled={
                        imageModal.currentIndex === imageModal.images.length - 1
                      }
                      className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors disabled:opacity-40"
                    >
                      <FiChevronRight className="w-6 h-6" />
                    </button>
                  </div>
                )}

                <p className="text-center text-sm font-medium text-white py-2">
                  {imageModal.title} - Image {imageModal.currentIndex + 1}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Modal - MOVED OUTSIDE image modal */}
      {cancelModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Cancel Challan
              </h3>
              <button
                onClick={() =>
                  setCancelModal({
                    open: false,
                    challanId: null,
                    remarks: "",
                  })
                }
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Please provide a reason for cancelling this challan.
              </p>
              <textarea
                value={cancelModal.remarks}
                onChange={(e) =>
                  setCancelModal({
                    ...cancelModal,
                    remarks: e.target.value,
                  })
                }
                rows="4"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white transition-all duration-200"
                placeholder="Enter cancellation remarks..."
                required
              />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() =>
                  setCancelModal({
                    open: false,
                    challanId: null,
                    remarks: "",
                  })
                }
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCancelChallan}
                disabled={!cancelModal.remarks.trim()}
                className="px-5 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Challan;
